import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Paper } from '@mui/material';
import { Lock as LockIcon, Home as HomeIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { 
  selectIsAuthenticated, 
  selectAuthLoading, 
  selectHasPermission,
  selectHasRole,
  checkAuthStatus,
  selectUser
} from '../../store/slices/authSlice';
import { PermissionKey, RoleName } from '../../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePermission?: PermissionKey;
  requirePermissions?: PermissionKey[]; // Require ANY of these permissions
  requireAllPermissions?: PermissionKey[]; // Require ALL of these permissions
  requireRole?: RoleName;
  requireRoles?: RoleName[]; // Require ANY of these roles
  requireAllRoles?: RoleName[]; // Require ALL of these roles
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  redirectTo?: string; // Custom redirect path
  showAccessDenied?: boolean; // Show access denied page instead of redirect
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePermission,
  requirePermissions,
  requireAllPermissions,
  requireRole,
  requireRoles,
  requireAllRoles,
  requireAdmin = false,
  requireSuperAdmin = false,
  redirectTo = '/',
  showAccessDenied = true,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectAuthLoading);
  const user = useAppSelector(selectUser);
  
  const hasPermission = useAppSelector(state => 
    requirePermission ? selectHasPermission(state, requirePermission) : true
  );
  
  const hasAnyPermission = useAppSelector(state => {
    if (!requirePermissions || requirePermissions.length === 0) return true;
    return requirePermissions.some(p => selectHasPermission(state, p));
  });
  
  const hasAllPermissions = useAppSelector(state => {
    if (!requireAllPermissions || requireAllPermissions.length === 0) return true;
    return requireAllPermissions.every(p => selectHasPermission(state, p));
  });
  
  const hasRole = useAppSelector(state => 
    requireRole ? selectHasRole(state, requireRole) : true
  );
  
  const hasAnyRole = useAppSelector(state => {
    if (!requireRoles || requireRoles.length === 0) return true;
    return requireRoles.some(r => selectHasRole(state, r));
  });
  
  const hasAllRoles = useAppSelector(state => {
    if (!requireAllRoles || requireAllRoles.length === 0) return true;
    return requireAllRoles.every(r => selectHasRole(state, r));
  });
  
  const isAdmin = useAppSelector(state => 
    requireAdmin ? selectHasRole(state, 'Admin') || selectHasRole(state, 'SuperAdmin') : true
  );
  
  const isSuperAdmin = useAppSelector(state => 
    requireSuperAdmin ? selectHasRole(state, 'SuperAdmin') : true
  );

  // Check auth status on mount
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(checkAuthStatus());
    }
  }, [dispatch, isAuthenticated]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // Check permissions and roles
  const hasAccess = hasPermission && 
                   hasAnyPermission && 
                   hasAllPermissions && 
                   hasRole && 
                   hasAnyRole && 
                   hasAllRoles && 
                   isAdmin && 
                   isSuperAdmin;

  if (!hasAccess) {
    if (!showAccessDenied) {
      return <Navigate to={redirectTo} replace />;
    }

    const getRequirementsList = () => {
      const requirements = [];
      if (requirePermission) requirements.push(`Permission: ${requirePermission}`);
      if (requirePermissions?.length) requirements.push(`Any of: ${requirePermissions.join(', ')}`);
      if (requireAllPermissions?.length) requirements.push(`All of: ${requireAllPermissions.join(', ')}`);
      if (requireRole) requirements.push(`Role: ${requireRole}`);
      if (requireRoles?.length) requirements.push(`Any role: ${requireRoles.join(', ')}`);
      if (requireAllRoles?.length) requirements.push(`All roles: ${requireAllRoles.join(', ')}`);
      if (requireAdmin) requirements.push('Admin access required');
      if (requireSuperAdmin) requirements.push('SuperAdmin access required');
      return requirements;
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 3,
          p: 3,
          backgroundColor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 500,
            borderRadius: 2,
          }}
        >
          <LockIcon
            sx={{
              fontSize: 64,
              color: 'error.main',
              mb: 2,
            }}
          />
          
          <Typography variant="h4" color="error" gutterBottom fontWeight="bold">
            Access Denied
          </Typography>
          
          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
            You don't have the required permissions to access this page.
          </Typography>
          
          {user && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current user: <strong>{user.username}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your roles: <strong>{user.roles?.map(r => r.name).join(', ') || 'None'}</strong>
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Required access:
            </Typography>
            {getRequirementsList().map((requirement, index) => (
              <Typography key={index} variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                • {requirement}
              </Typography>
            ))}
          </Box>
          
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => window.location.href = redirectTo}
            sx={{ mt: 2 }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
